import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { playerService } from "../../services/api";
import { formatDate } from "../../utils/playerValueHistory";
import { buildAbilityModel, band } from "../../utils/playerAbility";
import { radar } from "../../utils/playerAbilityRadar";

// Player Attributes tab — provider ability values as radar boards.
// Built 1:1 from the design handoff ("Attributes Tab.dc.html", binding spec):
// dark navy hero head with the comprehensive value, an overview radar, one
// radar card per category (plus a combined Physical/Defending card), and a
// "Further ratings" bar list. All sizing is driven by the CONTAINER width via
// ResizeObserver (same pattern as PlayerValue.js), not the window width.
// Hand-rolled inline SVG only; empty values render nothing.

const FF = "'Helvetica Neue',Helvetica,Arial,sans-serif";
const DOT = "·";
const ACCENT = "#2183FE";
const ACCENT_DARK = "#0B5FCB";

// Band labels — index matches band(v).i from the radar util.
const BAND_LABELS = ["Below 60", "60–74", "75–84", "85+"];

// Data key -> English source label (also the i18n key fed to t()).
const ATTR_LABELS = {
  mentality: "Mentality",
  attacking: "Attacking",
  defending: "Defending",
  habilidad: "Habilidad",
  physical: "Physical",
  goalkeeping: "Goalkeeping",
  movement: "Movement",
  aggression: "Aggression",
  interceptions: "Interceptions",
  positioning: "Positioning",
  vision: "Vision",
  penalties: "Penalties",
  composure: "Composure",
  crossing: "Crossing",
  finishing: "Finishing",
  heading: "Heading accuracy",
  shortpass: "Short passing",
  volleys: "Volleys",
  marking: "Marking",
  standing: "Standing tackle",
  sliding: "Sliding tackle",
  dribbling: "Dribbling",
  curve: "Curve",
  fkacc: "FK accuracy",
  longpass: "Long passing",
  ballcontrol: "Ball control",
  shotpower: "Shot power",
  jumping: "Jumping",
  stamina: "Stamina",
  strength: "Strength",
  longshots: "Long shots",
  accel: "Acceleration",
  sprint: "Sprint speed",
  agility: "Agility",
  reactions: "Reactions",
  balance: "Balance",
  gkdiving: "GK diving",
  gkhandling: "GK handling",
  gkkicking: "GK kicking",
  gkpositioning: "GK positioning",
  gkreflexes: "GK reflexes",
  anticipation: "Anticipation",
  control: "Control",
  aerial: "Aerial",
  creativity: "Creativity",
  pace: "Pace",
  passing: "Passing",
  shooting: "Shooting",
  diving: "Diving",
};

const labelFor = (t, key) => t(ATTR_LABELS[key] || key);

const skel = {
  background: "#EDF1F6",
  animation: "tpskel 1.4s ease-in-out infinite",
};

// ---- radar figure ----------------------------------------------------------
// Renders one radar (SVG grid/polygon/vertices + absolutely positioned HTML
// number chips and axis labels). `geom` is the contract object from the radar
// util; `axes` carry key/value/derived/active in the same order as the verts.
const RadarFigure = ({
  geom,
  axes,
  font,
  polyFill,
  polyStroke,
  t,
  interactive,
  hoverKey,
  onHoverKey,
}) => {
  if (!geom || !axes || axes.length === 0) return null;
  const { size, c } = geom;
  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        maxWidth: "100%",
        margin: "6px auto 0",
        font: `600 ${font}px ${FF}`,
      }}
    >
      <svg
        data-testid="tp-radar"
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        style={{ display: "block", width: "100%", height: "100%" }}
      >
        {geom.rings.map((ring, i) => (
          <polygon
            key={`r${i}`}
            points={ring.points}
            fill="none"
            stroke="#EDF1F6"
            strokeWidth="1"
          />
        ))}
        {geom.spokes.map((sp, i) => (
          <line
            key={`s${i}`}
            x1={c}
            y1={c}
            x2={sp.x2}
            y2={sp.y2}
            stroke="#EDF1F6"
            strokeWidth="1"
          />
        ))}
        <polygon
          points={geom.poly}
          fill={polyFill}
          stroke={polyStroke}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {geom.verts.map((v, i) => {
          const axis = axes[i];
          return (
            <g key={`v${i}`}>
              {v.active && (
                <circle cx={v.x} cy={v.y} r="12" fill="rgba(11,95,203,0.14)" />
              )}
              {v.derived && (
                <circle
                  cx={v.x}
                  cy={v.y}
                  r="8.5"
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="1.4"
                  strokeDasharray="2 2"
                />
              )}
              <circle
                cx={v.x}
                cy={v.y}
                r={v.active ? 5.5 : 3.4}
                fill={v.active ? ACCENT_DARK : ACCENT}
              />
              {interactive && axis && (
                <circle
                  data-testid={`tp-vertex-${axis.key}`}
                  cx={v.x}
                  cy={v.y}
                  r="14"
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => onHoverKey && onHoverKey(axis.key)}
                  onMouseLeave={() => onHoverKey && onHoverKey(null)}
                  onClick={() =>
                    onHoverKey &&
                    onHoverKey(hoverKey === axis.key ? null : axis.key)
                  }
                />
              )}
            </g>
          );
        })}
      </svg>
      {geom.labels.map((lb, i) => (
        <div
          key={`l${i}`}
          style={{
            position: "absolute",
            left: lb.x,
            top: lb.y,
            transform: `translate(${lb.tx},${lb.ty})`,
            // Clamp the label box to the radar canvas: the side labels of
            // tight cards would otherwise be clipped at the card edge
            // (overflow hidden). Wrapping beats truncation.
            maxWidth: Math.max(
              34,
              Math.min(
                lb.maxWidth,
                lb.align === "right"
                  ? lb.x
                  : lb.align === "left"
                    ? size - lb.x
                    : lb.maxWidth
              )
            ),
            textAlign: lb.align,
            color: "#64748B",
            fontSize: "0.87em",
            fontWeight: 500,
            lineHeight: 1.2,
            textWrap: "pretty",
            pointerEvents: "none",
          }}
        >
          {labelFor(t, lb.key)}
        </div>
      ))}
      {geom.nums.map((nm, i) =>
        nm.value == null ? null : (
          <div
            key={`n${i}`}
            style={{
              position: "absolute",
              left: nm.x,
              top: nm.y,
              transform: "translate(-50%,-50%)",
              padding: "0 3px",
              borderRadius: 3,
              background: "rgba(255,255,255,0.84)",
              color: "#0F172A",
              fontWeight: 700,
              whiteSpace: "nowrap",
              lineHeight: 1.25,
              fontVariantNumeric: "tabular-nums",
              pointerEvents: "none",
            }}
          >
            {nm.value}
          </div>
        )
      )}
    </div>
  );
};

// ---- presentational view (testable with fixture models) --------------------
export const PlayerAttributesView = ({
  model,
  width,
  t,
  loading,
  error,
  hoverKey,
  expandedKey,
  onHoverKey,
  onToggleExpand,
  onRetry,
}) => {
  const w = Number(width) || 1200;
  const small = w <= 430;
  const pad = small ? 16 : w <= 800 ? 24 : 32;
  const gap = small ? 12 : 20;
  const contentW = w - pad * 2;

  const pageStyle = {
    fontFamily: FF,
    background: "#F8FAFC",
    color: "#0F172A",
    padding: pad,
    boxSizing: "border-box",
    width: "100%",
  };

  const cols = w >= 2200 ? 4 : w >= 1900 ? 3 : w >= 700 ? 2 : 1;
  const gridStyle = {
    display: "grid",
    gap,
    marginTop: gap,
    gridTemplateColumns: `repeat(${cols},minmax(0,1fr))`,
    alignItems: "start",
  };

  // ---- loading skeleton ----
  if (loading) {
    const two = w >= 1024;
    const rs = Math.round(
      Math.min(two ? contentW * 0.62 : contentW, w >= 1900 ? 620 : 480)
    );
    const cardRadar = Math.round(
      Math.min((contentW - gap * (cols - 1)) / cols - 40, 400)
    );
    return (
      <div style={pageStyle}>
        <style>{`@keyframes tpskel { 0% { opacity:.55 } 50% { opacity:1 } 100% { opacity:.55 } }`}</style>
        <div
          style={{
            display: "grid",
            gap,
            gridTemplateColumns: two ? "minmax(300px,0.85fr) 2fr" : "1fr",
          }}
        >
          <div
            style={{
              borderRadius: 12,
              minHeight: small ? 150 : 230,
              ...skel,
            }}
          />
          <div
            style={{
              border: "1px solid #E2E8F0",
              borderRadius: 12,
              background: "#FFFFFF",
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "40%",
                height: 12,
                borderRadius: 6,
                alignSelf: "flex-start",
                ...skel,
              }}
            />
            <div
              style={{
                width: rs,
                height: rs,
                maxWidth: "100%",
                borderRadius: "50%",
                ...skel,
              }}
            />
          </div>
        </div>
        <div style={gridStyle}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                border: "1px solid #E2E8F0",
                borderRadius: 12,
                background: "#FFFFFF",
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ width: 110, height: 12, borderRadius: 6, ...skel }} />
                <div style={{ width: 44, height: 26, borderRadius: 6, ...skel }} />
              </div>
              <div
                style={{
                  width: cardRadar,
                  height: cardRadar,
                  maxWidth: "100%",
                  margin: "0 auto",
                  borderRadius: "50%",
                  ...skel,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- error (distinct copy from empty) ----
  if (error) {
    return (
      <div style={pageStyle}>
        <div
          style={{
            border: "1px solid #E2E8F0",
            borderRadius: 12,
            background: "#FFFFFF",
            padding: "56px 32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            textAlign: "center",
          }}
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#334155"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="7.5" x2="12" y2="13" />
            <circle cx="12" cy="16.6" r="0.9" fill="#334155" stroke="none" />
          </svg>
          <div style={{ font: `600 18px ${FF}`, color: "#0F172A" }}>
            {t("Ability data could not be loaded")}
          </div>
          <div
            style={{
              font: `400 14px/1.5 ${FF}`,
              color: "#64748B",
              maxWidth: 460,
              textWrap: "pretty",
            }}
          >
            {t(
              "The request to the ability provider failed. This is not the same as a player without ability data."
            )}
          </div>
          <button
            type="button"
            onClick={onRetry}
            className="hover:bg-[#0B5FCB]"
            style={{
              marginTop: 8,
              background: ACCENT,
              color: "#FFFFFF",
              border: 0,
              borderRadius: 8,
              padding: "12px 22px",
              minHeight: 44,
              font: `600 14px ${FF}`,
              cursor: "pointer",
            }}
          >
            {t("Retry")}
          </button>
        </div>
      </div>
    );
  }

  // ---- empty / insufficient: the ENTIRE tab is the empty state ----
  if (!model || !model.sufficient) {
    return (
      <div style={pageStyle}>
        <div
          style={{
            border: "1px dashed #94A3B8",
            borderRadius: 12,
            background: "#FFFFFF",
            padding: "56px 32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            textAlign: "center",
          }}
        >
          <svg
            width="34"
            height="34"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94A3B8"
            strokeWidth="1.4"
            aria-hidden="true"
          >
            <polygon points="12,3 21,8.5 21,15.5 12,21 3,15.5 3,8.5" />
            <line x1="12" y1="3" x2="12" y2="21" />
            <line x1="3" y1="8.5" x2="21" y2="15.5" />
            <line x1="21" y1="8.5" x2="3" y2="15.5" />
          </svg>
          <div style={{ font: `600 18px ${FF}`, color: "#0F172A" }}>
            {t("No ability data available for this player")}
          </div>
          <div
            style={{
              font: `400 14px/1.5 ${FF}`,
              color: "#64748B",
              maxWidth: 440,
              textWrap: "pretty",
            }}
          >
            {t(
              "The provider profile does not carry enough ability values to render this tab."
            )}
          </div>
          <div style={{ font: `400 12px ${FF}`, color: "#94A3B8", marginTop: 4 }}>
            {t("Provider-rated ability values, not a TransferPitch assessment.")}
          </div>
        </div>
      </div>
    );
  }

  // ---- full page ----
  const bandInfo = model.comprehensive != null ? band(model.comprehensive) : null;
  const collapsible = small;

  const twoCol = w >= 1024;
  const headW = twoCol ? Math.max(300, Math.min(contentW * 0.28, 420)) : contentW;
  const overviewW = twoCol ? contentW - headW - gap : contentW;
  const ovSize = Math.round(
    Math.max(
      280,
      Math.min(
        overviewW - 40,
        w >= 2200 ? 820 : w >= 1900 ? 700 : w >= 1280 ? 560 : 480
      )
    )
  );
  const ovFont = small ? 12 : ovSize > 600 ? 15 : 13;

  const overviewAxes = (model.overviewAxes || []).map((a) => ({
    ...a,
    active: a.key === hoverKey,
  }));
  const ovGeom =
    overviewAxes.length > 0
      ? radar(overviewAxes, ovSize, {
          pad: small ? 52 : ovSize > 600 ? 86 : 70,
          labelGap: small ? 18 : 26,
          numGap: small ? 13 : 17,
        })
      : null;

  const colW = (contentW - gap * (cols - 1)) / cols;
  const cardRadarSize = Math.round(
    Math.max(255, Math.min(colW - 40, w >= 2200 ? 480 : 420))
  );
  const cardFont = small ? 11.5 : cardRadarSize > 380 ? 13.5 : 12.5;

  const fcols = w >= 2200 ? 3 : w >= 1440 ? 2 : 1;
  const further = model.further || [];

  return (
    <div style={pageStyle}>
      <div
        style={{
          display: "grid",
          gap,
          gridTemplateColumns: twoCol
            ? `${Math.round(headW)}px minmax(0,1fr)`
            : "1fr",
          alignItems: "stretch",
        }}
      >
        {/* Hero head block */}
        <div
          style={{
            borderRadius: 12,
            padding: small ? "20px" : "24px 26px 26px",
            background:
              "linear-gradient(150deg,#08183A 0%,#123B7E 52%,#2183FE 100%)",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: small ? 0 : 240,
          }}
        >
          <div
            style={{
              font: `600 11px ${FF}`,
              letterSpacing: ".14em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,.72)",
            }}
          >
            {t("Overall ability")}
          </div>
          {model.comprehensive != null && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 10,
                marginTop: 6,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: small ? 62 : w >= 1900 ? 88 : 76,
                  fontFamily: FF,
                  color: "#FFFFFF",
                  lineHeight: 0.9,
                  letterSpacing: "-0.03em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {model.comprehensive}
              </div>
              <div
                style={{
                  fontWeight: 500,
                  fontSize: 15,
                  fontFamily: FF,
                  color: "rgba(255,255,255,.7)",
                  paddingBottom: 10,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                / 100
              </div>
            </div>
          )}
          {bandInfo && (
            <div
              style={{
                display: "inline-flex",
                alignSelf: "flex-start",
                whiteSpace: "nowrap",
                alignItems: "center",
                marginTop: 12,
                padding: "5px 11px",
                borderRadius: 999,
                background: "rgba(255,255,255,.16)",
                border: "1px solid rgba(255,255,255,.28)",
                font: `600 11px ${FF}`,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: "#FFFFFF",
              }}
            >
              {t(BAND_LABELS[bandInfo.i])}
            </div>
          )}
          <div
            style={{
              height: 1,
              background: "rgba(255,255,255,.18)",
              margin: "18px 0 12px",
            }}
          />
          {model.updatedAt != null && (
            <div
              style={{
                fontWeight: 400,
                fontSize: 12,
                fontFamily: FF,
                color: "rgba(255,255,255,.78)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {`${t("Last updated")} ${DOT} ${formatDate(model.updatedAt)}`}
            </div>
          )}
          <div
            style={{
              font: `400 11px/1.45 ${FF}`,
              color: "rgba(255,255,255,.58)",
              marginTop: 6,
              maxWidth: 340,
              textWrap: "pretty",
            }}
          >
            {t("Provider-rated ability values, not a TransferPitch assessment.")}
          </div>
        </div>

        {/* Overview radar card */}
        <div
          style={{
            border: "1px solid #E2E8F0",
            borderRadius: 12,
            background: "#FFFFFF",
            padding: 20,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ font: `600 15px ${FF}`, color: "#0F172A" }}>
              {t("Ability overview")}
            </div>
            <div
              style={{
                fontWeight: 400,
                fontSize: 12,
                fontFamily: FF,
                color: "#94A3B8",
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {`${overviewAxes.length} ${t("axes")}`}
            </div>
          </div>
          <RadarFigure
            geom={ovGeom}
            axes={overviewAxes}
            font={ovFont}
            polyFill="rgba(33,131,254,0.16)"
            polyStroke={ACCENT}
            t={t}
            interactive
            hoverKey={hoverKey}
            onHoverKey={onHoverKey}
          />
          {model.hasDerived && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 8,
                font: `400 12px ${FF}`,
                color: "#94A3B8",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <circle
                  cx="6"
                  cy="6"
                  r="4.5"
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="1.4"
                  strokeDasharray="2 2"
                />
              </svg>
              <span>{t("Axis value derived from its detail values.")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Category radar cards */}
      <div style={gridStyle}>
        {(model.cards || []).map((card) => {
          const parents = card.parents || [];
          const isCombined = card.key === "combined" && parents.length > 1;
          const open = collapsible ? expandedKey === card.key : true;
          const hl = !!(
            hoverKey &&
            (card.key === hoverKey ||
              (card.key === "combined" &&
                (card.parts || []).indexOf(hoverKey) >= 0))
          );
          const title = parents.map((pp) => labelFor(t, pp.key)).join(" & ");
          const geom = radar(card.axes || [], cardRadarSize, {
            pad: small ? 46 : cardRadarSize > 380 ? 62 : 54,
            labelGap: small ? 16 : 20,
            numGap: small ? 12 : 15,
          });
          return (
            <div
              key={card.key}
              data-testid={`tp-card-${card.key}`}
              style={{
                border: `1px solid ${hl ? ACCENT : "#E2E8F0"}`,
                borderRadius: 12,
                background: "#FFFFFF",
                padding: small ? "14px 14px 4px" : "18px 20px 8px",
                boxShadow: hl ? "0 0 0 3px rgba(33,131,254,0.14)" : "none",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <div
                data-testid={`tp-card-header-${card.key}`}
                role={collapsible ? "button" : undefined}
                onClick={
                  collapsible
                    ? () => onToggleExpand && onToggleExpand(card.key)
                    : undefined
                }
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 12,
                  paddingBottom: open ? 4 : 10,
                  cursor: collapsible ? "pointer" : "default",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      font: `600 ${small ? 14 : 15}px ${FF}`,
                      color: "#0F172A",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{
                      fontWeight: 400,
                      fontSize: 11,
                      fontFamily: FF,
                      color: "#94A3B8",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {`${(card.axes || []).length} ${t("axes")}`}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                    {parents.map((pp, i) => (
                      <div
                        key={pp.key}
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 7,
                        }}
                      >
                        {isCombined && (
                          <span style={{ font: `500 12px ${FF}`, color: "#64748B" }}>
                            {labelFor(t, pp.key)}
                          </span>
                        )}
                        {pp.value != null && (
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: isCombined
                                ? small
                                  ? 22
                                  : 26
                                : small
                                  ? 26
                                  : 32,
                              fontFamily: FF,
                              color: band(pp.value).color,
                              fontVariantNumeric: "tabular-nums",
                              lineHeight: 1,
                            }}
                          >
                            {pp.value}
                          </span>
                        )}
                        {isCombined && i < parents.length - 1 && (
                          <span style={{ font: `400 15px ${FF}`, color: "#94A3B8" }}>
                            {DOT}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  {collapsible && (
                    <svg
                      data-testid="tp-chevron"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="#64748B"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <polyline
                        points={open ? "4,10 8,6 12,10" : "4,6 8,10 12,6"}
                      />
                    </svg>
                  )}
                </div>
              </div>
              {open && (
                <RadarFigure
                  geom={geom}
                  axes={card.axes || []}
                  font={cardFont}
                  polyFill={hl ? "rgba(11,95,203,0.20)" : "rgba(33,131,254,0.16)"}
                  polyStroke={hl ? ACCENT_DARK : ACCENT}
                  t={t}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Further ratings */}
      {further.length > 0 && (
        <div
          style={{
            border: "1px solid #E2E8F0",
            borderRadius: 12,
            background: "#FFFFFF",
            padding: 20,
            marginTop: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div style={{ font: `600 15px ${FF}`, color: "#0F172A" }}>
              {t("Further ratings")}
            </div>
            <div style={{ font: `400 12px ${FF}`, color: "#94A3B8" }}>
              {t("Standalone provider values")}
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gap: small ? "12px" : "14px 32px",
              gridTemplateColumns: `repeat(${fcols},minmax(0,1fr))`,
            }}
          >
            {further.map((row) =>
              row.value == null ? null : (
                <div
                  key={row.key}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(90px,1.1fr) 2.2fr 34px",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      font: `500 13px ${FF}`,
                      color: "#334155",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {labelFor(t, row.key)}
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 4,
                      background: "#EDF1F6",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${row.value}%`,
                        height: "100%",
                        borderRadius: 4,
                        background: band(row.value).color,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FF,
                      color: "#0F172A",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {row.value}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- container (fetch + state) ---------------------------------------------
const PlayerAttributes = ({ playerId, primaryPosition }) => {
  const { t } = useTranslation();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  const [hoverKey, setHoverKey] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  // Container width drives every breakpoint (same pattern as PlayerValue.js).
  const shellRef = useRef(null);
  const [shellW, setShellW] = useState(1200);
  useLayoutEffect(() => {
    const el = shellRef.current;
    if (!el) return undefined;
    const measure = () => setShellW(el.clientWidth || 1200);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchAbility = async () => {
      if (!playerId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await playerService.getPlayerAbility(playerId);
        if (!alive) return;
        if (response.success) {
          setPayload(response.data || {});
        } else {
          setError(response.error || "Failed to fetch ability data");
          setPayload(null);
        }
      } catch (err) {
        console.error("Error fetching ability data:", err);
        if (alive) {
          setError("Failed to fetch ability data");
          setPayload(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchAbility();
    return () => {
      alive = false;
    };
  }, [playerId, fetchKey]);

  useEffect(() => {
    setHoverKey(null);
    setExpandedKey(null);
  }, [playerId]);

  const model = useMemo(
    () => (payload != null ? buildAbilityModel(payload, { primaryPosition }) : null),
    [payload, primaryPosition]
  );

  const handleToggleExpand = useCallback(
    (key) => setExpandedKey((cur) => (cur === key ? null : key)),
    []
  );
  const handleRetry = useCallback(() => setFetchKey((k) => k + 1), []);

  return (
    <div ref={shellRef} style={{ width: "100%" }}>
      <PlayerAttributesView
        model={model}
        width={shellW}
        t={t}
        loading={loading}
        error={error}
        hoverKey={hoverKey}
        expandedKey={expandedKey}
        onHoverKey={setHoverKey}
        onToggleExpand={handleToggleExpand}
        onRetry={handleRetry}
      />
    </div>
  );
};

export default PlayerAttributes;
